import React from 'react';
import { NavLink } from 'react-router-dom';
import type { MenuItem } from '../../services/navigation';
import { Menu as MenuIcon, ShoppingCart, Table, ChefHat, DollarSign } from 'lucide-react';

interface MobileNavProps {
  items: MenuItem[];
  onNavigate?: () => void;
  onMenuToggle?: () => void;
}

export function MobileNav({ onNavigate, onMenuToggle }: MobileNavProps) {
  // Acciones rápidas prioritarias para un flujo de trabajo profesional
  const quickActions = [
    { label: 'POS', path: '/pos', icon: <ShoppingCart size={22} /> },
    { label: 'Mesas', path: '/tables', icon: <Table size={22} /> },
    { label: 'Cocina', path: '/kitchen', icon: <ChefHat size={22} /> },
    { label: 'Caja', path: '/cash', icon: <DollarSign size={22} /> },
  ];

  return (
    <nav
      aria-label="Navegación móvil principal"
      className="lg:hidden"
      data-testid="mobile-nav"
    >
      <div
        className="fixed inset-x-0 bottom-0 z-40 backdrop-blur-xl supports-[backdrop-filter]:bg-surface/95"
        style={{
          background: 'rgba(var(--surface), 0.98)',
          borderTop: '1px solid rgba(var(--border), 0.8)',
          boxShadow: '0 -10px 40px rgba(0,0,0,0.08)',
          paddingBottom: 'max(env(safe-area-inset-bottom, 12px), 12px)',
          paddingTop: '8px',
          height: 'calc(68px + env(safe-area-inset-bottom, 12px))',
        }}
      >
        <ul className="flex w-full items-center justify-between px-3" role="menubar">
          {quickActions.map((item) => (
            <li key={item.path} className="flex-1" role="none">
              <NavLink
                to={item.path}
                onClick={onNavigate}
                role="menuitem"
                className={({ isActive }) =>
                  `relative flex flex-col items-center justify-center gap-1 transition-all duration-300 ${isActive ? 'text-primary' : 'text-text-muted hover:text-text'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <div
                      className={`flex h-10 w-12 items-center justify-center rounded-2xl transition-all duration-300 ${isActive
                          ? 'bg-primary/10'
                          : 'bg-transparent'
                        }`}
                    >
                      {React.cloneElement(item.icon as React.ReactElement, {
                        size: 24,
                        strokeWidth: isActive ? 2.5 : 2,
                        className: `transition-transform duration-300 ${isActive ? 'scale-110' : 'scale-100'}`
                      })}
                    </div>
                    <span className="text-[10px] font-bold tracking-tight">
                      {item.label}
                    </span>
                    {isActive && (
                      <span className="absolute -top-1 h-1 w-6 rounded-full bg-primary" />
                    )}
                  </>
                )}
              </NavLink>
            </li>
          ))}

          <li className="flex-1" role="none">
            <button
              type="button"
              onClick={onMenuToggle}
              className="group relative flex w-full flex-col items-center justify-center gap-1 text-text-muted transition-all duration-300 active:scale-90"
            >
              <div className="flex h-10 w-12 items-center justify-center rounded-2xl transition-colors group-hover:bg-muted/50">
                <MenuIcon size={24} strokeWidth={2} />
              </div>
              <span className="text-[10px] font-bold tracking-tight">
                Menú
              </span>
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
}
